import PersonIcon from "@mui/icons-material/Person";
import { Skeleton, Tooltip } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import ControlFilter from "components/shared/ControlFilter";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import MessageBlock from "components/shared/MessageBlock";
import ModalBase from "components/shared/ModalBase";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import TextButton from "components/shared/TextButton";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { IRepresentative } from "containers/Beneficiars/types";
import type { DeclarationsCardsData, URSSAFBeneficiaryError } from "containers/DeclarationPage/utils";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import type { IPayment } from "store/paymentSlice";
import { fetchURSSAFErrors } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";
import UpdatesModal from "../UpdatesModal";
import { formatRepresentativesNames, getBeneficiaryErrors, getReducedErrors, getReducedPayments } from "../utils";
import styles from "./style.module.scss";

interface IBeneficiariesTable {
    /**
     * All payments included in chosen period.
     */
    data: IPayment[];
    /**
     * URSSAF errors.
     */
    errors: URSSAFBeneficiaryError[];
    /**
     * Status of URSSAF errors fetch.
     */
    errorsStatus: TFetchStatus;
    handleNextStep: () => void;
    /**
     * Data of chosen declaration.
     */
    declarationData: IDeclaration | DeclarationsCardsData | undefined;
    /**
     * Date boundaries for declaration.
     */
    dateBoundaries: {
        startDate: string;
        endDate: string;
        quarter?: number | undefined;
    };
}

/**
 * Table within URSSAF DeclarationModal (publisher module) which displays info of beneficiaires concerned.
 */
const BeneficiariesTableStep = ({
    data,
    errors,
    errorsStatus,
    handleNextStep,
    declarationData,
    dateBoundaries,
}: IBeneficiariesTable) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { paymentsStatus } = useAppSelector((state) => state.payment);

    const [modalType, setModalType] = useState("");
    const [currentBeneficiary, setCurrentBeneficiary] = useState<IBeneficiar | undefined>();
    const [beneficiaryErrors, setBeneficiaryErrors] = useState<URSSAFBeneficiaryError | undefined>();
    const [query, setQuery] = useState("");
    const [filterValue, setFilterValue] = useState("all");
    const errorsMap = new Map<string, URSSAFBeneficiaryError>(
        errors.map((error) => [String(error.beneficiaryId), error]),
    );

    /**
     * Status rules needed for chip column.
     */
    const statusRules: Record<any, IStatusRule> = {
        valid: {
            color: "green",
            text: t("Declarations.BeneficiariesTable.Valid", { count: 1 }),
        },
        invalid: {
            color: "yellow",
            text: t("Declarations.BeneficiariesTable.Invalid", { count: 1 }),
        },
    };

    const formatStatusColumn = (payment: IPayment) => {
        const errorExists = getBeneficiaryErrors({ payment, errors: errorsMap });

        return (
            <StatusChip
                statusRules={statusRules}
                status={errorExists ? "invalid" : "valid"}
                tooltipText={errorExists ? t("Declarations.DeclareModal.StatusTooltip") : ""}
            />
        );
    };

    const formatBeneficiaryColumn = (beneficiary: IBeneficiar, payment: IPayment) => {
        const representative = payment._representative;

        return (
            <div className={styles["beneficiary-column"]}>
                <ManagementTableAuthorComponent
                    author={`${(beneficiary.surname ?? payment.name ?? "").toUpperCase()} ${beneficiary.name ?? ""}`}
                    avatarUrl={payment.beneficiaryAvatarUrl}
                    nameStyle={{ fontWeight: "400", textWrap: "wrap", cursor: "pointer" }}
                    beneficiaryId={beneficiary.id}
                    representative={
                        representative.id
                            ? `${(representative.surname ?? "").toUpperCase()} ${representative?.name ?? ""}`
                            : ""
                    }
                />
            </div>
        );
    };

    const handleBeneficiaryEdit = async (beneficiary: IBeneficiar, errors: URSSAFBeneficiaryError) => {
        await setCurrentBeneficiary(beneficiary);
        await setBeneficiaryErrors(errors);
        setModalType("beneficiaryForm");
    };

    const formatActionColumn = (payment: IPayment) => {
        const _errors = getBeneficiaryErrors({ payment, errors: errorsMap });

        if (_errors) {
            return (
                <TextButton
                    text={t("Declarations.BeneficiariesTable.Fix")}
                    handleClick={() => handleBeneficiaryEdit(payment.beneficiary, _errors)}
                    style={{ fontSize: "12px" }}
                    underlineOnHover
                />
            );
        } else {
            return "-";
        }
    };

    /**
     * Beneficiaries filtered based on search bar and control filters.
     */
    const filteredData = useMemo(() => {
        // First filter based on query:
        let filtered = data.filter(
            (item) =>
                (item.beneficiary.name ?? "").toLowerCase().includes(query) ||
                (item.beneficiary.surname ?? item.name ?? "").toLowerCase().includes(query),
        );

        // If control value is "anomaly", add extra filter on that:
        if (filterValue === "anomaly") {
            filtered = filtered.filter((benef) => {
                return getBeneficiaryErrors({ payment: benef, errors: errorsMap });
            });
        } else if (filterValue === "valid") {
            filtered = filtered.filter((benef) => {
                return !getBeneficiaryErrors({ payment: benef, errors: errorsMap });
            });
        }
        return filtered;
    }, [data, query, filterValue]);

    const beneficiariesColumns: LastElementTableColumns[] = [
        {
            id: "id",
            label: t("Declarations.BeneficiariesTable.Status"),
            align: "left",
            format: (value: number, id, currentPayment) => formatStatusColumn(currentPayment),
        },
        {
            id: "beneficiary",
            label: t("Declarations.BeneficiariesTable.Beneficiaries"),
            align: "left",
            format: (value: IBeneficiar, id: any, currentRow: IPayment) => {
                return formatBeneficiaryColumn(value, currentRow);
            },
        },
        {
            id: "netValue",
            label: t("Declarations.BeneficiariesTable.NetAmount"),
            align: "right",
            sort: true,
            format: (value: number, id: any, currentRow: IPayment) =>
                formatNumber((value - currentRow.netRemainingToPay) / 100, { isCurrency: true }),
        },
        {
            id: "contributions",
            label: t("Declarations.BeneficiariesTable.Contributions"),
            align: "right",
            format: (value: number) => formatNumber(value / 100, { isCurrency: true }),
        },
        {
            id: "date",
            label: t("Declarations.BeneficiariesTable.Date"),
            align: "right",
            format: (value: string) =>
                formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
            sort: true,
        },
        {
            id: "action",
            label: t("Declarations.BeneficiariesTable.Action"),
            align: "right",
            format: (value, id, currentRow: IPayment) => formatActionColumn(currentRow),
        },
    ];

    const errorsLoading = ["pending", "idle"].includes(errorsStatus);
    const paymentsLoading = ["pending", "idle"].includes(paymentsStatus);

    const bottomFooterContent = useMemo(() => {
        const reducedPayments = getReducedPayments(data);
        const validPayments = reducedPayments.length - getReducedErrors(errors).length;
        return (
            <div className={styles["footer-content"]}>
                {errorsLoading ? (
                    <Skeleton variant="text" width={50} />
                ) : data.length > 0 ? (
                    <span>
                        {t("Declarations.BeneficiariesTable.ValidBeneficiaries", {
                            number: `${validPayments}/${reducedPayments.length}`,
                        })}
                    </span>
                ) : (
                    <></>
                )}
            </div>
        );
    }, [data, errors]);

    const filterOptions = [
        { value: "all", label: t("Filters.AllMasculine") },
        { value: "valid", label: t("Declarations.BeneficiariesTable.Valid", { count: 2 }) },
        { value: "anomaly", label: t("Declarations.BeneficiariesTable.Invalid", { count: 2 }) },
    ];

    const handleErrorsDownload = async () => {
        dispatch(
            fetchURSSAFErrors({
                startDate: dateBoundaries.startDate,
                endDate: dateBoundaries.endDate,
                downloadMode: true,
            }),
        );
    };

    const updateModalTitle = useMemo(() => {
        if (!beneficiaryErrors) return "";
        const beneficiary = beneficiaryErrors.beneficiary;
        const representative = beneficiaryErrors.beneficiary._representative;

        let titleName = `${(beneficiary.surname ?? "").toUpperCase()} ${beneficiary?.name ?? ""}`;
        if (representative.id) {
            titleName += ` <> ${(representative.surname ?? "").toUpperCase()} ${representative?.name ?? ""}`;
        }
        return titleName;
    }, [beneficiaryErrors]);

    return (
        <div className={styles["urssaf-beneficiaries-table"]}>
            <div className={styles["table-header"]}>
                <h1>{t("Declarations.DeclareModal.TitleStep1")}</h1>
                <span>{t("Declarations.DeclareModal.SubtitleStep1")}</span>
            </div>
            <div className={styles["table-filters"]}>
                <SmallSearchBar filter={query} onSearch={(e) => setQuery(e.target.value)} />
                <ControlFilter
                    filterOptions={filterOptions}
                    controlValue={filterValue}
                    onValueChange={(value) => setFilterValue(value)}
                />
            </div>
            <LastElementTable
                data={filteredData}
                columns={beneficiariesColumns}
                max={9999}
                maxHeight={300}
                minHeight={300}
                isLoading={errorsLoading || paymentsLoading}
                bottomFooterContent={bottomFooterContent}
                dataType="urssafBeneficiaries"
                columnsNumber={6}
            />
            {!errorsLoading && errors?.length > 0 && (
                <MessageBlock
                    text={t("Declarations.BeneficiariesTable.BeneficiariesWarning")}
                    type="warning"
                    style={{ maxWidth: "755px", margin: "0 auto", marginTop: "15px" }}
                />
            )}
            {!errorsLoading && !paymentsLoading && data.length === 0 && (
                <span className={styles["no-data-info"]}>{t("Declarations.BeneficiariesTable.NoData")}</span>
            )}
            <div className={styles["action-buttons"]}>
                <Tooltip
                    title={
                        !errorsLoading && errors?.length === 0
                            ? t("Declarations.BeneficiariesTable.NoErrorsTooltip")
                            : ""
                    }
                    arrow
                >
                    <div>
                        <StyledButton
                            buttonType="secondary"
                            onClick={handleErrorsDownload}
                            disabled={errorsLoading || (!errorsLoading && errors?.length === 0)}
                        >
                            {t("Declarations.BeneficiariesTable.DownloadAnomalies")}
                        </StyledButton>
                    </div>
                </Tooltip>
                <StyledButton buttonType="primary" onClick={handleNextStep} disabled={!data.length && errorsLoading}>
                    {t("Declarations.BeneficiariesTable.Continue")}
                </StyledButton>
            </div>
            <ModalBase
                modalTitle={updateModalTitle}
                modalSubtitle={t("Declarations.BeneficiariesTable.ModalSubtitle")}
                open={modalType === "beneficiaryForm"}
                onClose={() => setModalType("")}
                maxHeight={"95%"}
                contentStyle={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
                <UpdatesModal
                    beneficiaryId={currentBeneficiary?.id}
                    beneficiaryErrors={beneficiaryErrors}
                    declarationData={declarationData}
                    handleClose={() => setModalType("")}
                />
            </ModalBase>
        </div>
    );
};

export default BeneficiariesTableStep;
